// npm
import React from 'react'
import {connect} from 'react-redux'

// react
import {WorkingsTabs} from './workings-tabs'
import {Calculator} from './calculator'
import {MaterialScrollWrapper} from 'components/layout/scroll_wrapper/material-scroll-wrapper'

// redux (selectors)
import {getContentData} from 'redux/reducers/exam/selectors'
import {getExamData, getSettingsData} from 'redux/reducers/selectors'
import {getClientSettingsData} from 'redux/reducers/settings/selectors'
import {getCalculatorEvaluationMode, getWorkingsCalcMode} from 'redux/reducers/settings/client/selectors'
import {getQuestionDataById} from 'redux/reducers/exam/content/questions/selectors'
import {getWorkingsData} from 'redux/reducers/exam/content/questions/question/selectors'
import {getCurrentQuestionId, getQuestionsData} from 'redux/reducers/exam/content/selectors'
import {getWorkings} from 'redux/reducers/exam/content/questions/question/workings/selectors'

// redux (actions)
import {saveWorkings} from './actions'

import check from 'check-types';
import { addCalculatorSymbols } from './calculator-helper'
import { MIN_CONTENT_WIDTH } from '../../../constants'

// must fit the calculator: 4 buttons x (40px SINGLE_BUTTON_WIDTH + 16px margins) = 224, plus the
// calculator container's 8px side margins and 1px borders = 242 - anything narrower overflows
const DEFAULT_WIDTH = 244;
const MIN_WIDTH = DEFAULT_WIDTH;

// width reserved for the left question-navigation panel (see side/panels/navigation/panel.jsx wrapperStyle) -
// kept as a constant here since desktop.jsx has no way to tell this panel how much room is actually left
const LEFT_PANEL_RESERVED_WIDTH = 220;

// WorkingsPanel (not connected to store)
// ------------------------------------------------------

const setFocusPosition=(elem,caretPos)=>{
  if(elem != null) {
    if(elem.createTextRange) {
        var range = elem.createTextRange();
        range.move('character', caretPos);
        range.select();
    }
    else {
        try{
            elem.focus();
            elem.setSelectionRange(caretPos, caretPos);
        }
        catch(e){
            elem.focus();
        }
    }
}  
}

class WorkingsPanel extends React.Component {
  constructor(props) {
    super(props);
    this.workingsRef = React.createRef();
    this.setFocus = false;
    this.state = { width: props.initialWidth || DEFAULT_WIDTH };
    this.handleWorkingsChange = this.handleWorkingsChange.bind(this);
    this.handleCalculatorResult = this.handleCalculatorResult.bind(this);
    this.handleResizeStart = this.handleResizeStart.bind(this);
  }

  componentDidUpdate() {
    if (this.setFocus) {
      const workingsElement = this.workingsRef.current;
      if (workingsElement){
        if (this.cursorPosition) {
          setFocusPosition(workingsElement, this.cursorPosition);
          this.cursorPosition = null;
        }
        else workingsElement.focus();
      }
      this.setFocus = false;
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);
  }

  handleWorkingsChange(value, setFocus = false) {
    if (check.number(setFocus)){
      this.setFocus = true;
      this.cursorPosition=setFocus;
    }
    else{
      this.setFocus = setFocus;
    }
    this.props.onChange(value);
  }

  handleCalculatorResult(_result) {
    const result = addCalculatorSymbols(_result);
    const { value } = this.props;
    const workingsElement = this.workingsRef.current;
    const { selectionStart, selectionEnd } = workingsElement;

    this.cursorPosition = selectionStart + result.length + 1;

    const workingsBeforeSelection = value.substring(0, selectionStart);
    const workingsAfterSelection = value.substring(selectionEnd);
    const newValue = `${workingsBeforeSelection}${result}\n${workingsAfterSelection}`;
    
    this.handleWorkingsChange(newValue, true);
  }

  handleResizeStart(e) {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = this.state.width;

    this._onMouseMove = (e) => {
      // drag handle is on the left edge; moving left increases width
      // capped so the panel + question + nav can't exceed the window and trigger the layout's drawer fallback
      const widthThatFitsAlongsideQuestion = window.innerWidth - MIN_CONTENT_WIDTH - LEFT_PANEL_RESERVED_WIDTH;
      const maxWidth = Math.max(MIN_WIDTH, Math.min(Math.floor(window.innerWidth * 0.5), widthThatFitsAlongsideQuestion));
      const newWidth = Math.min(maxWidth, Math.max(MIN_WIDTH, startWidth + (startX - e.clientX)));
      this.setState({ width: newWidth });
      if (this.props.onWidthChange) {
        this.props.onWidthChange(newWidth);
      }
    };

    this._onMouseUp = () => {
      document.removeEventListener('mousemove', this._onMouseMove);
      document.removeEventListener('mouseup', this._onMouseUp);
    };

    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mouseup', this._onMouseUp);
  }  

  render() {
    const { workingsCalcMode } = this.props;
    const resizable = workingsCalcMode === 1;
    const width = resizable ? this.state.width : DEFAULT_WIDTH;

    const workingsProps = {
      value: this.props.value,
      workingsRef: this.workingsRef,
      onChange: this.handleWorkingsChange
    };

    const wrapperDivStyle = {
      display: 'flex',
      width,
      height: '100%',
      boxSizing: 'border-box',
      flexDirection: 'column',
      position: 'relative'      
    };

    const resizeHandleStyle = {
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: 4,
      cursor: 'col-resize',
      zIndex: 1,
      userSelect: 'none'
    };

    return (
      <MaterialScrollWrapper horizontal={false}>
        <div style={wrapperDivStyle}>
          {resizable && (
            <div style={resizeHandleStyle} onMouseDown={this.handleResizeStart}/>
          )}
          <WorkingsTabs {...workingsProps} />
          {this.Calculator}
        </div>
      </MaterialScrollWrapper>
    );
  }

  get Calculator() {
    if (this.props.disableCalculator) {
      return null;
    }
    
    let {evaluationType} = this.props;
    if (!check.assigned(evaluationType)) evaluationType = 1;

    const props = {
      onResult: this.handleCalculatorResult,      
      evaluationType
    }
    return <Calculator {...props} />;
  }
}

// WorkingsPanel (connected to store)
// ------------------------------------------------------

const mapStoreToProps = store => {
  const contentData = getContentData(getExamData(store));
  const questionsData = getQuestionsData(contentData);
  const currentQuestionId = getCurrentQuestionId(contentData);
  const questionData = getQuestionDataById(questionsData)(currentQuestionId);

  const settingsData = getSettingsData(store);
  const clientSettingsData = getClientSettingsData(settingsData);

  return {
    value: getWorkings(getWorkingsData(questionData)),
    evaluationType: getCalculatorEvaluationMode(clientSettingsData),
    workingsCalcMode: getWorkingsCalcMode(clientSettingsData),
    currentQuestionId
  };
};

const mapDispatchToProps = dispatch => ({
  onChange: questionId => value => {
    dispatch(saveWorkings(questionId, value));
  }
});

const mergeProps = ({value, currentQuestionId, evaluationType, workingsCalcMode}, {onChange}, ownProps) => ({
  onChange: onChange(currentQuestionId),
  disableCalculator: ownProps.disableCalculator,
  evaluationType,
  workingsCalcMode,
  value,
  initialWidth: ownProps.initialWidth,
  onWidthChange: ownProps.onWidthChange,
});

const args = [mapStoreToProps, mapDispatchToProps, mergeProps];
WorkingsPanel = connect(...args)(WorkingsPanel);


// Export
// ------------------------------------------------------
export {WorkingsPanel}